import moment from "moment";
import { Badge, BadgeSize } from "shared/components";
import { capitalize } from "shared/utils";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { Row } from "views/applications-id-report/components/Row";
import { DynamicKeyValue } from "views/applications-id-report/components/SmartRender/DynamicKeyValue";

import { VaccinationTable } from "./VaccinationTable";
import { PetScreeningAnimal } from "../../types/pet-screening";
import { statusBadgeVariant } from "../../utils";
import { PetVerificationStatusMap } from "../../constants";

interface PetScreeningReportFieldsProps {
  animals: PetScreeningAnimal[];
}

export const PetScreeningReportFields: React.FC<
  PetScreeningReportFieldsProps
> = ({ animals }: PetScreeningReportFieldsProps) => {
  return (
    <>
      {animals.map((animal, index) => {
        return (
          <div key={`container-${animal.name}-${index}`}>
            <ContentTitle
              title={`Pet / Assistance animal ${index + 1}`}
              key={`content-${animal.name}-${index}`}
              contentClassName="space-y-3"
            >
              <DynamicKeyValue
                detailsKey="Creation date"
                fullDate
                value={
                  animal.created_at
                    ? moment(animal.created_at).format(
                        "MMM DD, YYYY [at] h:mm A"
                      )
                    : undefined
                }
              />
              <DynamicKeyValue
                detailsKey="Profile kind"
                value={
                  animal.type
                    ? capitalize(animal.type.toLowerCase().replace("_", " "))
                    : "--"
                }
              />
              <DynamicKeyValue detailsKey="Name" value={animal.name} />
              <DynamicKeyValue detailsKey="Species" value={animal.species} />
              <Row
                title="Status"
                value={
                  <Badge
                    className="shrink-0 items-center"
                    size={BadgeSize.SM}
                    variant={statusBadgeVariant(animal.status)}
                  >
                    {PetVerificationStatusMap[animal.status] || animal.status}
                  </Badge>
                }
              />
              <DynamicKeyValue
                detailsKey="Fido score"
                value={animal.fido_score?.toString() || "--"}
              />
              <DynamicKeyValue
                detailsKey="Dominant breed"
                value={animal.dominant_breed || "--"}
              />
              <DynamicKeyValue
                detailsKey="Secondary breed"
                value={animal.secondary_breed || "--"}
              />
              <DynamicKeyValue
                detailsKey="Sex"
                value={animal.sex ? capitalize(animal.sex.toLowerCase()) : "--"}
              />
              <DynamicKeyValue
                detailsKey="Neutered/spayed status"
                value={animal.neutered_spayed}
              />
              <DynamicKeyValue
                detailsKey="DOB"
                value={
                  animal.birth_date
                    ? moment(animal.birth_date).format("MMM D, YYYY")
                    : undefined
                }
                fullDate
              />
              <DynamicKeyValue
                detailsKey="Date of ownership"
                value={
                  animal.date_of_ownership
                    ? moment(animal.date_of_ownership).format("MMM D, YYYY")
                    : undefined
                }
                fullDate
              />
              <DynamicKeyValue
                detailsKey="Weight"
                value={
                  animal.weight?.value
                    ? `${animal.weight.value}${animal.weight.unit}`
                    : undefined
                }
              />
              <DynamicKeyValue
                detailsKey="Coat color(s)"
                value={animal.coat_colors?.join(", ") || "--"}
              />
              <DynamicKeyValue
                detailsKey="Is service animal?"
                value={animal.is_service_animal}
              />
              <DynamicKeyValue
                detailsKey="My pet is vaccinated and all required shots are valid to date"
                value={animal.is_vaccinated}
              />
            </ContentTitle>
            <VaccinationTable vaccines={animal.vaccination} />
          </div>
        );
      })}
    </>
  );
};
